import React, { useEffect, useMemo, useRef } from 'react';
import useScrollRestoration from 'utils/scrollRestoration';
import { useRouter } from 'next/router';
import Head from 'next/head';
import Router from 'next/router';
import PropTypes from 'prop-types';
import getAppTheme from '@/constants/theme';
import { ScrollRestoreProvider } from 'context/ScrollRestoreContext';

import { ThemeProvider as MUIThemeProvider, CssBaseline } from '@mui/material';
import { Provider } from 'react-redux';
import { useDispatch, useSelector } from 'react-redux';
import store from '@/context/store';
import MainLayout from '@/layouts/MainLayout';
import AdminLayout from '@/layouts/AdminLayout';
import '../styles/globals.css';
import '../styles/tailwind.css';
import '../styles/editor.css';
import '@/i18n';
import i18n from '@/i18n';
import Cookies from 'js-cookie';
import { setAccount, setUser } from 'context/actions/auth';

import ProgressBar from '@badrap/bar-of-progress';

const progress = new ProgressBar({
    size: 3,
    color: '#0B2B5C',
    className: 'bar-of-progress',
    delay: 100,
});

Router.events.on('routeChangeStart', progress.start);
Router.events.on('routeChangeComplete', progress.finish);
Router.events.on('routeChangeError', progress.finish);

function AppInitializer() {
    const dispatch = useDispatch();
    const lang = useSelector((s) => s.system?.lang);

    useEffect(() => {
        // Arkhad-ийн зарчмаар — cookie-аас хэл уншиж i18n шинэчлэх
        if (typeof window !== 'undefined') {
            const chosenLang = Cookies.get('chosen_lang') || 'mn';
            i18n.changeLanguage(chosenLang);
            dispatch({ type: 'CHANGE_LANG', data: chosenLang });
        }

        setAllUser();
    }, []);

    // ── Хэл өөрчлөгдөх бүрт <html lang="..."> шинэчлэх ──
    // SEO, font fallback, screen reader-д чухал
    useEffect(() => {
        if (typeof window !== 'undefined' && lang) {
            document.documentElement.lang = lang;
        }
    }, [lang]);

    const setAllUser = async () => {
        try {
            const userRaw = localStorage.getItem('user');
            if (userRaw) {
                const user = JSON.parse(userRaw);
                if (user) setUser(dispatch, user);
            }
            const accountRaw = localStorage.getItem('account');
            if (accountRaw) {
                const account = JSON.parse(accountRaw);
                if (account) setAccount(dispatch, account);
            }
        } catch (e) {
            console.error('AppInitializer error', e);
        }
    };

    return null;
}

function KhureeApp({ Component, pageProps }) {
    // Scroll restoration системд: Ref-ийг useScrollRestoration-тай
    // ScrollRestoreProvider-т нэгэн зэрэг дамжуулна.
    // Browser back/forward товчоор буцахад, data бэлэн болсны дараа
    // (notifyScrollReady дуудагдах үед) scroll байршил буцна.
    const pendingRestoreRef = useRef(null);
    useScrollRestoration(pendingRestoreRef);

    const router = useRouter();

    // MUI theme (light mode)
    const theme = useMemo(() => getAppTheme('light'), []);

    // Admin хуудаснууд AdminLayout-тай. Зөвхөн /admin (login) хуудас layout-гүй.
    const isAdminRoute = router.pathname.startsWith('/admin');
    const isAdminLogin = router.pathname === '/admin';

    const getLayout =
        Component.getLayout ||
        ((page) => {
            if (isAdminLogin) return page; // Нэвтрэх хуудас layout-гүй
            if (isAdminRoute) return <AdminLayout>{page}</AdminLayout>;
            return <MainLayout>{page}</MainLayout>;
        });

    return (
        <Provider store={store}>
            <Head>
                {/* ── Үндсэн ── */}
                <title>Хүрээ Тулга сургууль — Хүүхэд бүр өөрийнхөөрөө гэрэлтэнэ</title>
                <meta
                    name="viewport"
                    content="width=device-width, initial-scale=1, maximum-scale=5"
                />
                <meta charSet="utf-8" />
                <meta
                    name="description"
                    content="Нийслэлийн Ерөнхий Боловсролын Хүрээ Тулга сургууль — Баянгол дүүрэг, 6-р бичил хороолол. Хүүхэд бүр өөрийнхөөрөө гэрэлтэнэ."
                />
                <meta
                    name="keywords"
                    content="Хүрээ Тулга, Huree Tulga, сургууль, боловсрол, Улаанбаатар, Баянгол, ерөнхий боловсрол, 한국어, 몽골 학교"
                />
                <meta name="author" content="Хүрээ Тулга сургууль" />
                <meta name="robots" content="index, follow" />
                <meta name="theme-color" content="#0B2B5C" />

                {/* ── Favicon цуглуулга ── */}
                <link rel="icon" href="/favicon.ico" sizes="any" />
                <link rel="icon" type="image/png" sizes="16x16" href="/favicon-16x16.png" />
                <link rel="icon" type="image/png" sizes="32x32" href="/favicon-32x32.png" />
                <link rel="icon" type="image/png" sizes="96x96" href="/favicon-96x96.png" />
                <link rel="apple-touch-icon" sizes="180x180" href="/apple-touch-icon.png" />
                <link rel="manifest" href="/site.webmanifest" />

                {/* ── Open Graph (Facebook, Messenger, LinkedIn) ── */}
                <meta property="og:type" content="website" />
                <meta property="og:site_name" content="Хүрээ Тулга сургууль" />
                <meta
                    property="og:title"
                    content="Хүрээ Тулга сургууль — Хүүхэд бүр өөрийнхөөрөө гэрэлтэнэ"
                />
                <meta
                    property="og:description"
                    content="Нийслэлийн Ерөнхий Боловсролын Хүрээ Тулга сургууль — Баянгол дүүрэг, 6-р бичил хороолол."
                />
                <meta property="og:url" content="https://hureeschool.com" />
                <meta property="og:image" content="https://hureeschool.com/og-image.jpg" />
                <meta property="og:image:width" content="1200" />
                <meta property="og:image:height" content="630" />
                <meta property="og:locale" content="mn_MN" />
                <meta property="og:locale:alternate" content="ko_KR" />
                <meta property="og:locale:alternate" content="en_US" />

                {/* ── Twitter Card ── */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content="Хүрээ Тулга сургууль" />
                <meta
                    name="twitter:description"
                    content="Нийслэлийн Ерөнхий Боловсролын Хүрээ Тулга сургууль — Хүүхэд бүр өөрийнхөөрөө гэрэлтэнэ"
                />
                <meta name="twitter:image" content="https://hureeschool.com/og-image.jpg" />

                {/* ── Canonical ── */}
                <link rel="canonical" href="https://hureeschool.com" />
            </Head>
            <ScrollRestoreProvider onReadyRef={pendingRestoreRef}>
                <MUIThemeProvider theme={theme}>
                    <CssBaseline />
                    <AppInitializer />
                    {getLayout(<Component {...pageProps} />)}
                </MUIThemeProvider>
            </ScrollRestoreProvider>
        </Provider>
    );
}

KhureeApp.propTypes = {
    Component: PropTypes.elementType.isRequired,
    pageProps: PropTypes.object.isRequired,
};

export default KhureeApp;